import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SearchableCombobox,
} from "@nexus/ui";
import type { PromptDto } from "@/lib/queries";
import {
  GLOBAL_TENANT_OPTION_ID,
  usePromptTenantOptions,
  type PromptTenantOption,
} from "@/features/prompts/hooks/usePromptTenantOptions";
import { usePromptActions } from "@/features/prompts/hooks/usePromptActions";

interface DuplicatePromptDialogProps {
  prompt: PromptDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A prompt is unique per (call site, tenant), so a copy has to land in a
 * different scope than its source — this dialog is where that gets chosen.
 *
 * Mounted only while open (see PromptsPage). Form state is seeded once at
 * mount so a parent re-render with a new `prompt` object identity cannot wipe
 * a tenant the user just picked — that previously submitted as Global and 409'd.
 */
export function DuplicatePromptDialog({
  prompt,
  open,
  onOpenChange,
}: DuplicatePromptDialogProps) {
  const { t } = useTranslation();
  const tenantOptions = usePromptTenantOptions();
  const { duplicate } = usePromptActions();

  const [name, setName] = useState(() =>
    t("prompts.duplicate.defaultName", "{{name}} (copy)", { name: prompt.name }),
  );
  const [usedIn, setUsedIn] = useState(prompt.usedIn);
  const [tenantId, setTenantId] = useState<string | null>(prompt.tenantId);

  const isSameScope = tenantId === prompt.tenantId && usedIn.trim() === prompt.usedIn;
  const hasContent = Boolean(prompt.latestVersion?.content);

  const blockedReason = !hasContent
    ? t("prompts.duplicate.noContent", "This prompt has no saved version to copy.")
    : !name.trim() || !usedIn.trim()
      ? t("prompts.duplicate.required", "Name and call site are required.")
      : isSameScope
        ? t(
            "prompts.duplicate.sameScope",
            "Pick a different tenant or call site — each pair can hold only one prompt.",
          )
        : null;

  const handleSubmit = () => {
    duplicate.mutate(
      { source: prompt, name: name.trim(), usedIn: usedIn.trim(), tenantId },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{t("prompts.duplicate.title", "Duplicate prompt")}</DialogTitle>
          <DialogDescription>
            {t(
              "prompts.duplicate.description",
              "Copies the latest template, model, and variables into a new call site or tenant.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicate-name" className="text-[12px] text-muted-foreground">
              {t("prompts.editor.name", "Name")}
            </Label>
            <Input
              id="duplicate-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="h-8 text-[13px]"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicate-used-in" className="text-[12px] text-muted-foreground">
              {t("prompts.editor.usedIn", "Used In")}
            </Label>
            <Input
              id="duplicate-used-in"
              value={usedIn}
              onChange={(event) => setUsedIn(event.target.value)}
              className="h-8 font-mono text-[12px]"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[12px] text-muted-foreground">
              {t("prompts.editor.tenant", "Tenant")}
            </Label>
            <SearchableCombobox<PromptTenantOption>
              items={tenantOptions}
              getKey={(tenant) => tenant.id}
              getLabel={(tenant) => tenant.name}
              value={tenantId === null ? GLOBAL_TENANT_OPTION_ID : tenantId}
              onSelect={(tenant) =>
                setTenantId(tenant.id === GLOBAL_TENANT_OPTION_ID ? null : tenant.id)
              }
              placeholder={t("prompts.editor.tenantGlobal", "Global (no tenant)")}
              searchPlaceholder={t("prompts.editor.tenantSearch", "Search tenants…")}
              triggerClassName="h-8 w-full text-[13px]"
              closeOnSelect
            />
          </div>

          <p role="status" className="min-h-4 text-[11px] text-warning-subtle-foreground">
            {blockedReason}
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={blockedReason !== null || duplicate.isPending}
            onClick={handleSubmit}
          >
            {duplicate.isPending
              ? t("prompts.duplicate.duplicating", "Duplicating…")
              : t("prompts.duplicate.confirm", "Duplicate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
